import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { t } from "@lingui/macro";
import * as PhosphorIcons from "@phosphor-icons/react";
import { ArrowsClockwise, PencilSimple, Plus } from "@phosphor-icons/react";
import type { SectionItem, SectionKey, SectionWithItem } from "@stylingcv/schema";
import { defaultSections } from "@stylingcv/schema";
import { Button, Tooltip } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { AnimatePresence } from "framer-motion";
import get from "lodash.get";
import { useState } from "react";
import { createPortal } from "react-dom";

import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { IconPickerModal } from "./icon-picker-modal";
import { SectionIcon } from "./section-icon";
import { SectionListItem } from "./section-list-item";
import { SectionOptions } from "./section-options";

type Props<T extends SectionItem> = {
  id: SectionKey;
  title: (item: T) => string;
  description?: (item: T) => string | undefined;
  date?: (item: T) => string | undefined;
  icon?: (item: T) => string | undefined;
  iconFamily?: "phosphor" | "hobby" | "icomoon";
  level?: (item: T) => number | undefined;
  keywords?: (item: T) => string[] | undefined;
};

// Helper function to get default section based on ID
const getDefaultSection = (id: SectionKey) => {
  if (id.startsWith("custom.")) {
    return {
      name: "Custom Section",
      visible: true,
      items: [],
    };
  }

  // Use the standard section from defaultSections if available, or create a fallback
  return (
    (defaultSections as any)[id] || {
      name: id.charAt(0).toUpperCase() + id.slice(1),
      visible: true,
      items: [],
    }
  );
};

const SECTION_HINTS: Partial<Record<string, string>> = {
  experience: "Add your work history. Lead with measurable impact.",
  education: "Add your academic background.",
  skills: "Add your professional skills.",
  projects: "Add personal or professional projects.",
};

export const SectionBase = <T extends SectionItem>({
  id,
  title,
  description,
  date,
  icon,
  iconFamily = "phosphor",
  level,
  keywords,
}: Props<T>) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [iconModalOpen, setIconModalOpen] = useState(false);
  const { open } = useDialog(id);

  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore((state) => {
    try {
      const sectionData = get(state.resume.data.sections, id);
      return sectionData || getDefaultSection(id);
    } catch {
      return getDefaultSection(id);
    }
  }) as SectionWithItem<T>;

  // Primitive selector so SectionBase re-renders immediately when the icon string changes
  const headerIconName = useResumeStore((state) => {
    try {
      return (get(state.resume.data.sections, id) as any)?.icon ?? "";
    } catch {
      return "";
    }
  });

  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const defaultSectionName = t`Custom Section`;
  const sectionName = section?.name || defaultSectionName;

  if (!section) return null;

  const hint = SECTION_HINTS[id];

  const onDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const onDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      try {
        const oldIndex = section.items.findIndex((item) => item.id === active.id);
        const newIndex = section.items.findIndex((item) => item.id === over.id);

        const sortedList = arrayMove(section.items as T[], oldIndex, newIndex);
        setValue(`sections.${id}.items`, sortedList);
      } catch {
        // Silent fail
      }
    }
  };

  const onDragCancel = () => {
    setActiveId(null);
  };

  const onCreate = () => {
    open("create", { id });
  };

  const onUpdate = (item: T) => {
    open("update", { id, item });
  };

  const onDuplicate = (item: T) => {
    open("duplicate", { id, item });
  };

  const onDelete = (item: T) => {
    open("delete", { id, item });
  };

  const onToggleVisibility = (index: number) => {
    try {
      const visible = get(section, `items[${index}].visible`, true);
      setValue(`sections.${id}.items[${index}].visible`, !visible);
    } catch {
      // Silent fail
    }
  };

  // Ensure items is always an array
  const items = section.items || [];

  // Derive header icon from the reactive primitive — bypasses the stale section-object reference issue
  const DynamicHeaderIcon = headerIconName
    ? ((PhosphorIcons as Record<string, any>)[kebabToPascal(headerIconName.trim())] ?? null)
    : null;

  return (
    <div id={id} className="flex h-full flex-col overflow-y-auto">
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-[var(--border,#e5e5e5)] px-6 py-5">
        {/* Icon box — click to open visual icon picker */}
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Tooltip content={t`Change icon`} side="bottom">
            <Button
              size="icon"
              variant="ghost"
              className="group/iconbtn relative flex size-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 transition-colors hover:bg-gray-200"
              onClick={() => {
                setIconModalOpen(true);
              }}
            >
              {DynamicHeaderIcon ? (
                <DynamicHeaderIcon size={18} weight="regular" />
              ) : (
                <SectionIcon id={id} size={18} className="" />
              )}
              {/* Hover badge — bottom-right corner */}
              <span className="pointer-events-none absolute -bottom-1 -right-1 flex size-[18px] items-center justify-center rounded-full border-2 border-white bg-[var(--accent,#268599)] opacity-0 transition-opacity duration-150 group-hover/iconbtn:opacity-100">
                <ArrowsClockwise size={9} weight="bold" color="white" />
              </span>
            </Button>
          </Tooltip>
        </div>

        {/* Title + hint */}
        <div className="min-w-0 flex-1">
          {isRenaming ? (
            <input
              autoFocus
              className="w-full border-b-2 border-[var(--accent,#268599)] bg-transparent text-2xl font-bold leading-tight text-[var(--fg-1,#171717)] outline-none"
              value={sectionName}
              onChange={(e) => {
                setValue(`sections.${id}.name`, e.target.value);
              }}
              onBlur={() => {
                setIsRenaming(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Escape") setIsRenaming(false);
              }}
            />
          ) : (
            <h2 className="truncate text-2xl font-bold leading-tight text-[var(--fg-1,#171717)]">
              {sectionName}
            </h2>
          )}
          {hint && !isRenaming && <p className="mt-1 text-xs text-[var(--fg-3,#737373)]">{hint}</p>}
        </div>

        {/* Pencil rename button */}
        <button
          className="shrink-0 rounded p-1.5 text-gray-400 transition-colors hover:text-gray-600"
          title={t`Rename section`}
          onClick={() => {
            setIsRenaming(true);
          }}
        >
          <PencilSimple size={16} />
        </button>

        {/* Section options (⋯ menu) */}
        <div
          className="shrink-0"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <SectionOptions id={id} />
        </div>
      </header>

      {/* Content */}
      <main
        className={cn(
          "flex flex-col p-5 transition-opacity @container",
          !section.visible && "opacity-50",
        )}
      >
        {items.length === 0 && (
          <button
            className="group flex w-full items-center justify-between rounded-xl border border-dashed border-[var(--border,#e5e5e5)] p-3 transition-colors hover:border-[var(--accent,#268599)] focus-visible:outline-none @[280px]:px-4 @[360px]:px-[18px] @[360px]:py-[14px]"
            onClick={onCreate}
          >
            <div className="flex items-center gap-3">
              <span className="flex size-7 items-center justify-center rounded-full bg-[var(--accent,#268599)] text-white transition-colors group-hover:bg-[var(--scv-teal-deep,#155469)]">
                <Plus size={14} weight="bold" />
              </span>
              <span className="text-[13px] font-medium text-[var(--fg-2,#404040)] transition-colors group-hover:text-[var(--accent,#268599)]">
                {t`Add a new item`}
              </span>
            </div>
            <span className="hidden rounded border border-[var(--border,#e5e5e5)] px-[6px] py-[3px] text-[10px] font-semibold text-[var(--fg-4,#a3a3a3)] @[280px]:inline">
              ⌘N
            </span>
          </button>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToParentElement]}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <AnimatePresence>
              {items.map((item, index) => (
                <SectionListItem
                  key={item.id}
                  id={item.id}
                  visible={item.visible ?? true}
                  title={title(item as T)}
                  description={description?.(item as T)}
                  date={date?.(item as T)}
                  icon={icon?.(item as T)}
                  iconFamily={iconFamily}
                  level={level?.(item as T)}
                  keywords={keywords?.(item as T)}
                  onUpdate={() => {
                    onUpdate(item as T);
                  }}
                  onDelete={() => {
                    onDelete(item as T);
                  }}
                  onDuplicate={() => {
                    onDuplicate(item as T);
                  }}
                  onToggleVisibility={() => {
                    onToggleVisibility(index);
                  }}
                />
              ))}
            </AnimatePresence>
          </SortableContext>

          {createPortal(
            <DragOverlay
              dropAnimation={{ duration: 200, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}
            >
              {activeId
                ? (() => {
                    const activeItem = items.find((item) => item.id === activeId);
                    if (!activeItem) return null;
                    return (
                      <SectionListItem
                        isDragOverlay
                        id={activeItem.id}
                        visible={activeItem.visible ?? true}
                        title={title(activeItem as T)}
                        description={description?.(activeItem as T)}
                        icon={icon?.(activeItem as T)}
                        iconFamily={iconFamily}
                      />
                    );
                  })()
                : null}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>
      </main>

      {/* Footer — Add new item (dashed-border design) */}
      {items.length > 0 && (
        <footer className="p-5 pt-0">
          <button
            className="group flex w-full items-center justify-between rounded-xl border border-dashed border-[var(--border,#e5e5e5)] p-3 transition-colors hover:border-[var(--accent,#268599)] focus-visible:outline-none @[280px]:px-4 @[360px]:px-[18px] @[360px]:py-[14px]"
            onClick={onCreate}
          >
            <div className="flex items-center gap-3">
              <span className="flex size-7 items-center justify-center rounded-full bg-[var(--accent,#268599)] text-white transition-colors group-hover:bg-[var(--scv-teal-deep,#155469)]">
                <Plus size={14} weight="bold" />
              </span>
              <span className="text-[13px] font-medium text-[var(--fg-2,#404040)] transition-colors group-hover:text-[var(--accent,#268599)]">
                {t`Add a new item`}
              </span>
            </div>
            <span className="hidden rounded border border-[var(--border,#e5e5e5)] px-[6px] py-[3px] text-[10px] font-semibold text-[var(--fg-4,#a3a3a3)] @[280px]:inline">
              ⌘N
            </span>
          </button>
        </footer>
      )}

      <IconPickerModal
        open={iconModalOpen}
        currentIcon={headerIconName}
        onSelect={(kebab) => {
          setValue(`sections.${id}.icon`, kebab);
        }}
        onClose={() => {
          setIconModalOpen(false);
        }}
      />
    </div>
  );
};

function kebabToPascal(str: string): string {
  return str
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
}
